// AlertsActivity.js
import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import { TbAlertOctagonFilled } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';

const Users = ({ 
  alerts, 
  recentActivities, 
  alertsLoading, 
  userType, 
  selectedLabId, 
  selectedLabName, 
  userName,
  filteredCases 
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError('');
    
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Fetch ALL users without any lab filter
      let url = `${process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com'}/api/auth/users-by-lab`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch users');
      }

      if (data.success && Array.isArray(data.users)) {
        // Filter only admin users (case-insensitive)
        const adminUsers = data.users.filter(user => {
          const role = (user.role || '').toLowerCase();
          return role === 'admin';
        });
        setUsers(adminUsers);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersError(err.message);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Function to generate full domain URL from subdomain
  const generateFullDomain = (subdomain) => {
    if (!subdomain || subdomain === '') return null;
    const cleanSubdomain = subdomain.split('.')[0];
    return `${cleanSubdomain}.hms.hiragroupcloud.com`;
  };

  // Get lab name from lab ID
  const getLabName = (labId) => {
    if (!labId) return 'N/A';
    return labId;
  };

  // Handle user row click - navigate to /users and store clicked user's labId as userLabId
  const handleUserClick = (user) => {
    // Get the clicked user's labId
    const clickedUserLabId = user.labId;
    
    if (clickedUserLabId) {
      // Store the clicked user's labId in localStorage as userLabId
      localStorage.setItem('userLabId', clickedUserLabId);
      
      // Also store labName for consistency with Navbar functionality
      const labNameToStore = user.labName || user.labId || clickedUserLabId;
      localStorage.setItem('labName', labNameToStore);
      
      // Store complete lab data similar to Navbar's approach
      localStorage.setItem('labData', JSON.stringify({
        id: clickedUserLabId,
        name: labNameToStore
      }));
      
      // Remove the clear flag since we're selecting a lab from Users page
      localStorage.removeItem('clearLabOnDashboard');
      
      console.log(`User clicked: ${user.firstName || user.first_name} ${user.lastName || user.last_name || ''}`);
      console.log(`Stored userLabId: ${clickedUserLabId}`);
      console.log(`Stored labName: ${labNameToStore}`);
      
      // Dispatch event to notify Navbar about lab change
      window.dispatchEvent(new Event('labChanged'));
    } else {
      console.warn('Clicked user has no labId');
    }
    
    // Navigate to /users path
    navigate('/users');
  };

  // ✅ Modified: Clear selected lab before going to Add New Lab
  const handleAddLab = () => {
    // Clear lab data
    localStorage.removeItem('userLabId');
    localStorage.removeItem('labName');
    localStorage.removeItem('labData');
    // Remove clear flag
    localStorage.removeItem('clearLabOnDashboard');

    // Notify Navbar instantly
    window.dispatchEvent(new Event('labChanged'));

    console.log('✅ Lab cleared successfully');

    // Navigate
    navigate('/Addnewuser');
  };
  
  return (
    <div style={{
      borderRadius: '16px',
      padding: isMobile ? '1rem' : '1.5rem',
      position: 'relative',
      overflow: 'hidden',
      background: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      {/* HEADER */}
      <h2 style={{
        background: '#004aad',
        color: 'white',
        padding: isMobile ? '10px 16px' : '14px 20px',
        borderRadius: '12px 12px 0 0',
        margin: isMobile ? '-1rem -1rem 1rem -1rem' : '-1.8rem -1.8rem 1.5rem -1.8rem',
        fontWeight: '600',
        fontSize: isMobile ? '1rem' : '1.1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TbAlertOctagonFilled size={isMobile ? 18 : 20} /> 
          <span>Labs</span>
        </div>
      </h2>

      {/* Users Section - Direct display without tabs */}
      <div style={{ padding: '0.5rem 0' }}>
        {/* Header with user count only */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <h3 style={{
              fontWeight: '600',
              margin: 0,
              fontSize: isMobile ? '0.9rem' : '1rem',
              color: '#1e293b'
            }}>
             Labs
            </h3>
            <span style={{
              fontSize: '0.8rem',
              color: '#64748b',
              backgroundColor: '#f1f5f9',
              padding: '4px 10px',
              borderRadius: '12px'
            }}>
              {loadingUsers ? 'Loading...' : `${users.length} admins`}
            </span>
          </div>
               {/* Right Side Add Lab Button */}
          <button
            onClick={handleAddLab}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: '#004aad',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: isMobile ? '8px 12px' : '10px 16px',
              fontSize: '0.85rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: '0.2s ease',
              boxShadow: '0 2px 6px rgba(0,74,173,0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#00337a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#004aad';
            }}
          >
            <FaPlus size={12} />
            Add Lab
          </button>

        </div>

        {/* Users Table */}
        {users.length === 0 && !loadingUsers ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem 1rem',
            color: '#94a3b8',
            fontStyle: 'italic',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            {usersError || 'No admin users found'}
          </div>
        ) : loadingUsers ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem 1rem',
            color: '#64748b'
          }}>
            Loading admin users...
          </div>
        ) : (
          <div style={{ 
            overflowX: 'auto',
            maxHeight: isMobile ? '300px' : '380px',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f8fafc'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.85rem',
              backgroundColor: 'white'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#e8f0fe',
                  borderBottom: '2px solid #004aad',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                    First Name
                  </th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                    Domain
                  </th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                    Lab Name
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  const fullDomain = generateFullDomain(user.subdomain);
                  const firstName = user.firstName || user.first_name || 'N/A';
                  
                  return (
                    <tr
                      key={user.userId || user._id || index}
                      style={{
                        borderBottom: index !== users.length - 1 ? '1px solid #e2e8f0' : 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'  // Show pointer cursor to indicate clickable row
                      }}
                      onClick={() => handleUserClick(user)}  // Handle click on entire row
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                        e.currentTarget.style.transform = 'scale(1.002)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <td style={{ padding: '12px 10px', fontWeight: '500', color: '#1e293b' }}>
                        {firstName} {user.lastName || user.last_name || ''}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        {fullDomain ? (
                          <a
                            href={`https://${fullDomain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#004aad',
                              textDecoration: 'none',
                              fontSize: '0.8rem',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all'
                            }}
                            onClick={(e) => e.stopPropagation()}  // Prevent row click when clicking domain link
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {fullDomain}
                          </a>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>
                            No domain
                          </span>
                        )}
                      </td>
             
                      <td style={{ padding: '12px 10px', fontSize: '0.8rem', color: '#666' }}>
                        {user.labName || getLabName(user.labId) || user.labId || 'N/A'}
                       </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Custom scrollbar styling */}
        <style dangerouslySetInnerHTML={{
          __html: `
            div::-webkit-scrollbar {
              width: 6px;
            }
            div::-webkit-scrollbar-track {
              background: #f8fafc;
              border-radius: 10px;
            }
            div::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }
            div::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `
        }} />
      </div>
    </div>
  );
};

export default Users;